import React, { useState, useEffect } from 'react';
import { getSpiritualFamily, addFamilyMember, deleteFamilyMember, createFollowup } from '../../services/api';
import { FiX, FiPlus, FiTrash2 } from 'react-icons/fi';

const currentYear = new Date().getFullYear();
const calcAge = (birthYear) => birthYear && !isNaN(birthYear) ? currentYear - parseInt(birthYear) : null;

const FamilyDetail = ({ familyId, onClose, showToast }) => {
  const [family, setFamily] = useState(null);
  const [tab, setTab] = useState('members');
  const [addingMember, setAddingMember] = useState(false);
  const [addingFollowup, setAddingFollowup] = useState(false);
  const [newMember, setNewMember] = useState({ name: '', role: 'child', birth_year: '', spiritual_status: 'unknown' });
  const [newFollowup, setNewFollowup] = useState({ date: new Date().toISOString().slice(0, 10), notes: '', family_status: 'unknown', next_followup_date: '' });
  const [saving, setSaving] = useState(false);

  const loadFamily = () => getSpiritualFamily(familyId).then(r => setFamily(r.data || null)).catch(() => {});

  useEffect(() => { loadFamily(); }, [familyId]);

  const handleAddMember = async () => {
    if (!newMember.role) return;
    setSaving(true);
    try {
      const age = calcAge(newMember.birth_year);
      await addFamilyMember(familyId, {
        name: newMember.name,
        role: newMember.role,
        age: age,
        birth_year: newMember.birth_year ? parseInt(newMember.birth_year) : null,
        spiritual_status: newMember.spiritual_status,
      });
      setAddingMember(false);
      setNewMember({ name: '', role: 'child', birth_year: '', spiritual_status: 'unknown' });
      loadFamily();
      showToast('Member added');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const handleDeleteMember = async (id) => {
    if (!window.confirm('Remove this family member?')) return;
    await deleteFamilyMember(id);
    loadFamily();
    showToast('Member removed', 'error');
  };

  const handleAddFollowup = async () => {
    if (!newFollowup.date) return;
    setSaving(true);
    try {
      await createFollowup({ family_id: familyId, ...newFollowup });
      setAddingFollowup(false);
      setNewFollowup({ date: new Date().toISOString().slice(0, 10), notes: '', family_status: 'unknown', next_followup_date: '' });
      loadFamily();
      showToast('Follow-up recorded');
    } catch (e) { showToast(e.response?.data?.error || 'Failed', 'error'); }
    finally { setSaving(false); }
  };

  const setM = field => e => setNewMember(p => ({ ...p, [field]: e.target.value }));
  const setF = field => e => setNewFollowup(p => ({ ...p, [field]: e.target.value }));

  const previewAge = calcAge(newMember.birth_year);

  if (!family) return null;

  return (
    <div className="modal-overlay">
      <div className="modal" style={{ maxWidth: 720 }}>
        <div className="modal-header">
          <div>
            <h3>{family.family_name}</h3>
            <p>Spiritual Father: {family.father_name || 'Not assigned'}{family.father_phone ? ` • ${family.father_phone}` : ''}</p>
          </div>
          <button className="btn-icon" onClick={onClose}><FiX /></button>
        </div>

        <div style={{ display: 'flex', borderBottom: '1px solid #e5e7eb', padding: '0 24px' }}>
          {['members', 'followups'].map(t => (
            <button key={t} onClick={() => setTab(t)} style={{
              padding: '10px 16px', border: 'none', background: 'none', cursor: 'pointer',
              fontWeight: 600, fontSize: 13, color: tab === t ? '#4f46e5' : '#6b7280',
              borderBottom: tab === t ? '2px solid #4f46e5' : '2px solid transparent',
              textTransform: 'capitalize'
            }}>{t}</button>
          ))}
        </div>

        <div className="modal-body">
          {tab === 'members' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ fontSize: 13 }}>Family Members ({family.members?.length || 0})</strong>
                <button className="btn btn-sm btn-secondary" onClick={() => setAddingMember(true)}><FiPlus /> Add Dependent</button>
              </div>

              {addingMember && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div className="form-grid">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Role</label>
                      <select value={newMember.role} onChange={setM('role')}>
                        <option value="head">Head</option>
                        <option value="wife">Wife</option>
                        <option value="child">Child</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Full Name</label>
                      <input value={newMember.name} onChange={setM('name')} placeholder="Full name" />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>
                        Birth Year
                        {previewAge !== null && (
                          <span style={{ fontWeight: 400, color: '#4f46e5', marginLeft: 8 }}>→ Age: {previewAge}</span>
                        )}
                      </label>
                      <input
                        type="number"
                        value={newMember.birth_year}
                        onChange={setM('birth_year')}
                        placeholder={`e.g. ${currentYear - 10}`}
                        min="1900"
                        max={currentYear}
                      />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Spiritual Status</label>
                      <select value={newMember.spiritual_status} onChange={setM('spiritual_status')}>
                        <option value="unknown">Unknown</option>
                        <option value="saved">Saved</option>
                        <option value="not_saved">Not Saved</option>
                        <option value="backslidden">Backslidden</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-sm btn-primary" onClick={handleAddMember} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setAddingMember(false)}>Cancel</button>
                  </div>
                </div>
              )}

              <table>
                <thead>
                  <tr><th>Role</th><th>Name</th><th>Birth Year</th><th>Age</th><th>Spiritual Status</th><th></th></tr>
                </thead>
                <tbody>
                  {(family.members || []).length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', color: '#9ca3af', padding: 24 }}>No members added yet</td></tr>
                  ) : (family.members || []).map(m => {
                    const age = m.birth_year ? currentYear - parseInt(m.birth_year) : m.age;
                    return (
                      <tr key={m.id}>
                        <td><span className="pill pill-purple" style={{ textTransform: 'capitalize' }}>{m.role}</span></td>
                        <td>{m.name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || '—'}</td>
                        <td>{m.birth_year || '—'}</td>
                        <td>{age !== null && age !== undefined ? `${age} yrs` : '—'}</td>
                        <td style={{ textTransform: 'capitalize' }}>{m.spiritual_status?.replace('_', ' ') || '—'}</td>
                        <td><button className="btn-icon danger" onClick={() => handleDeleteMember(m.id)}><FiTrash2 /></button></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {tab === 'followups' && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <strong style={{ fontSize: 13 }}>Follow-up History ({family.followups?.length || 0})</strong>
                <button className="btn btn-sm btn-secondary" onClick={() => setAddingFollowup(true)}><FiPlus /> Record Follow-up</button>
              </div>

              {addingFollowup && (
                <div style={{ background: '#f8fafc', border: '1px solid #e5e7eb', borderRadius: 8, padding: 16, marginBottom: 16 }}>
                  <div className="form-grid">
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Date <span className="required">*</span></label>
                      <input type="date" value={newFollowup.date} onChange={setF('date')} />
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Family Status</label>
                      <select value={newFollowup.family_status} onChange={setF('family_status')}>
                        <option value="unknown">Unknown</option>
                        <option value="good">Good</option>
                        <option value="needs_attention">Needs Attention</option>
                        <option value="critical">Critical</option>
                      </select>
                    </div>
                    <div className="form-group" style={{ marginBottom: 0 }}>
                      <label>Next Follow-up Date</label>
                      <input type="date" value={newFollowup.next_followup_date} onChange={setF('next_followup_date')} />
                    </div>
                    <div className="form-group form-full" style={{ marginBottom: 0 }}>
                      <label>Notes</label>
                      <textarea value={newFollowup.notes} onChange={setF('notes')} rows={2} />
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                    <button className="btn btn-sm btn-primary" onClick={handleAddFollowup} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
                    <button className="btn btn-sm btn-secondary" onClick={() => setAddingFollowup(false)}>Cancel</button>
                  </div>
                </div>
              )}

              {(family.followups || []).length === 0 ? (
                <div className="empty-state"><p>No follow-ups recorded yet</p></div>
              ) : (family.followups || []).map(fu => (
                <div key={fu.id} style={{ padding: '12px 0', borderBottom: '1px solid #f3f4f6' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                      <strong style={{ fontSize: 13 }}>{fu.date}</strong>
                      {fu.family_status && (
                        <span className={`pill ${fu.family_status === 'good' ? 'pill-green' : fu.family_status === 'critical' ? 'pill-red' : 'pill-yellow'}`} style={{ marginLeft: 8 }}>
                          {fu.family_status.replace('_', ' ')}
                        </span>
                      )}
                    </div>
                    <span style={{ fontSize: 12, color: '#6b7280' }}>by {fu.done_by_name || 'Unknown'}</span>
                  </div>
                  {fu.notes && <p style={{ fontSize: 13, color: '#374151', marginTop: 4 }}>{fu.notes}</p>}
                  {fu.next_followup_date && <p style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Next: {fu.next_followup_date}</p>}
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default FamilyDetail;
